import { styled, Theme } from '@mui/material/styles';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { useFormContext } from 'react-hook-form';
import { colorPalette } from '@repo/mui-config/color-palette';
import { Input, Select, FormDatePicker, PostalCodeInput } from '@repo/ui';

type Option = {
  id: number;
  name: string;
  value: string;
};

interface EditableRowProps {
  label: string;
  fieldName: string;
  fieldType: string;
  index: number;
  options?: Option[];
}

const textFieldStyles = (theme: Theme) => ({
  '& .MuiInput-root': {
    padding: 0,
    color: theme.palette.ink.light,
    WebkitTextFillColor: theme.palette.ink.light,
    transition: 'all 0.2s',

    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: `1px solid ${theme.palette.primary.main}`,
      transform: 'scaleX(0)',
      transition: 'transform 0.2s ease-out',
    },
    '&:hover:not(.Mui-disabled)::before': {
      borderBottom: 'none',
    },
    '&.Mui-focused': {
      color: 'inherit',
      WebkitTextFillColor: 'inherit',
    },
    '&.Mui-focused::after': {
      transform: 'scaleX(1)',
    },
    '&.Mui-disabled': {
      color: theme.palette.ink.light,
      WebkitTextFillColor: theme.palette.ink.light,
    },
  },
});

const CssTextField = styled(Input)(({ theme }) => textFieldStyles(theme));

const TextField = ({ fieldName }: { fieldName: string }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClickEditMode = () => inputRef.current?.focus();

  const handleMouseDownEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (inputRef.current && inputRef.current.value.trim() === '') {
      inputRef.current.focus();
    }
  }, []);

  return (
    <CssTextField
      name={fieldName}
      size="small"
      inputRef={inputRef}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={'run edit mode'}
                onClick={handleClickEditMode}
                onMouseDown={handleMouseDownEdit}
                onMouseUp={handleMouseUpEdit}
                edge="end"
              >
                <ModeEditIcon />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

const SelectField = ({ fieldName, options }: { fieldName: string; options: Option[] }) => {
  const [focused, setFocused] = useState(false);
  const iconComponent = options.length === 1 ? () => null : undefined;

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  return (
    <Select
      name={fieldName}
      options={options}
      disabled={options.length === 1}
      size="small"
      variant="outlined"
      IconComponent={iconComponent}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '& .MuiSelect-select': {
          padding: 0,
          color: !focused ? colorPalette.ink.light : undefined,
          WebkitTextFillColor: !focused ? colorPalette.ink.light : undefined,
        },
        '& .MuiSelect-select.Mui-disabled': {
          color: colorPalette.ink.light,
          WebkitTextFillColor: colorPalette.ink.light,
        },
        '& .MuiOutlinedInput-input': {
          padding: 0,
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'transparent',
        },
      }}
    />
  );
};

const StyledDatePicker = styled(FormDatePicker)(({ theme }) => ({
  '& .MuiInput-root': {
    padding: 0,
    color: theme.palette.ink.light,
    WebkitTextFillColor: theme.palette.ink.light,
    transition: 'all 0.2s',
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: `1px solid ${theme.palette.primary.main}`,
      transform: 'scaleX(0)',
      transition: 'transform 0.2s ease-out',
    },
    '&.Mui-error::after': {
      borderColor: theme.palette.error.main,
    },
    '&:hover:not(.Mui-disabled)::before': {
      borderBottom: 'none',
    },
    '&.Mui-focused': {
      color: 'inherit',
      WebkitTextFillColor: 'inherit',
    },
    '&.Mui-focused::after': {
      transform: 'scaleX(1)',
    },
    '&.Mui-disabled': {
      color: theme.palette.ink.light,
      WebkitTextFillColor: theme.palette.ink.light,
    },
  },
}));

const DateField = ({ fieldName }: { fieldName: string }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const context = useFormContext();
  const dateValue = context.watch(fieldName);

  useEffect(() => {
    if (!dateValue) {
      inputRef.current?.focus();
    }
  }, []);

  return (
    <StyledDatePicker
      name={fieldName}
      format="YYYY-MM-DD"
      disableFuture
      slotProps={{
        textField: {
          variant: 'standard',
          fullWidth: true,
          placeholder: 'RRRR-MM-DD',
          inputRef,
        },
        openPickerButton: {
          'aria-label': 'otwórz kalendarz',
        },
      }}
    />
  );
};

const CssPostalCodeInput = styled(PostalCodeInput)(({ theme }) => textFieldStyles(theme));

const PostalCode = ({ fieldName }: { fieldName: string }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClickEditMode = () => inputRef.current?.focus();

  const handleMouseDownEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpEdit = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (inputRef.current && inputRef.current?.value?.trim() === '') {
      inputRef.current.focus();
    }
  }, []);

  return (
    <CssPostalCodeInput
      name={fieldName}
      countryCode="PL"
      size="small"
      variant="standard"
      inputRef={inputRef}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={'run edit mode'}
                onClick={handleClickEditMode}
                onMouseDown={handleMouseDownEdit}
                onMouseUp={handleMouseUpEdit}
                edge="end"
              >
                <ModeEditIcon />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export const EditableRow = ({ label, fieldName, fieldType, index, options }: EditableRowProps) => {
  return (
    <Box
      sx={{
        paddingX: '24px',
        paddingTop: '16px',
        paddingBottom: '13px',
        backgroundColor: index % 2 === 0 ? colorPalette.white : colorPalette.cloud.light,
        borderRadius: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <Typography sx={{ width: '50%' }}>{label}</Typography>
      {fieldType === 'text' ? <TextField fieldName={fieldName} /> : null}
      {fieldType === 'select' && options ? (
        <SelectField fieldName={fieldName} options={options} />
      ) : null}
      {fieldType === 'date' ? <DateField fieldName={fieldName} /> : null}
      {fieldType === 'postCode' ? <PostalCode fieldName={fieldName} /> : null}
    </Box>
  );
};
